import axios from "axios";
import React from "react";

// Use env var when the site is served from the *same* host that the env var
// points to (i.e. normal Emergent preview / direct emergent.host access).
// When the site is served from a different host (e.g. sizzhka.shop mirror /
// val.town proxy for CIS users), prefer same-origin API calls so requests
// keep going through the mirror instead of hitting the blocked backend host
// directly from the browser.
const _envBackend = process.env.REACT_APP_BACKEND_URL || "";
let _apiOrigin = _envBackend || window.location.origin;
try {
  if (_envBackend) {
    const envHost = new URL(_envBackend).host;
    if (window.location.host && window.location.host !== envHost) {
      _apiOrigin = window.location.origin;
    }
  }
} catch (_e) { /* leave default */ }
const BACKEND_URL = _apiOrigin;
export const getBackendUrl = () => BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Separate axios client for customer (storefront) endpoints — uses customer_token
const customerApi = axios.create({ baseURL: API_BASE });
customerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("customer_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
// Auto-clear stale token on 401 so the UI can reflect the logged-out state.
customerApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && localStorage.getItem("customer_token")) {
      localStorage.removeItem("customer_token");
      try { window.dispatchEvent(new Event("customer-auth-changed")); } catch (_e) { /* ignore */ }
    }
    return Promise.reject(err);
  }
);

// Customer (storefront) auth
export const customerVerifyStart = () => axios.post(`${API_BASE}/customer/verify/start`).then(r => r.data);
export const customerVerifyStatus = (token) => axios.get(`${API_BASE}/customer/verify/status/${token}`).then(r => r.data);
export const customerMe = () => customerApi.get("/customer/me").then(r => r.data);
export const customerLogout = () => {
  localStorage.removeItem("customer_token");
  try { window.dispatchEvent(new Event("customer-auth-changed")); } catch (_e) { /* ignore */ }
};
export const getCustomerToken = () => localStorage.getItem("customer_token");
export const setCustomerToken = (t) => {
  if (t) {
    localStorage.setItem("customer_token", t);
    try { window.dispatchEvent(new Event("customer-auth-changed")); } catch (_e) { /* ignore */ }
  }
};
export const customerRegister = (data) => axios.post(`${API_BASE}/customer/register`, data).then(r => r.data);
export const customerLogin = (data) => axios.post(`${API_BASE}/customer/login`, data).then(r => r.data);
export const customerGetMessages = () => customerApi.get("/customer/messages").then(r => r.data);
export const customerSendMessage = (text) => customerApi.post("/customer/messages", { text }).then(r => r.data);
export const customerUnreadCount = () => customerApi.get("/customer/messages/unread_count").then(r => r.data);
export const submitSupport = (data) => axios.post(`${API_BASE}/support/request`, data).then(r => r.data);

// Admin customers (CRM)
export const adminListCustomers = (params) => api.get("/admin/customers", { params }).then(r => r.data);
export const adminCustomerDetail = (id) => api.get(`/admin/customers/${id}`).then(r => r.data);
export const adminSendCustomerMessage = (id, text) => api.post(`/admin/customers/${id}/message`, { text }).then(r => r.data);
export const adminDeleteCustomer = (id) => api.delete(`/admin/customers/${id}`).then(r => r.data);
// Admin site messages
export const adminMessagesThreads = () => api.get("/admin/messages/threads").then(r => r.data);
export const adminMessagesUnreadCount = () => api.get("/admin/messages/unread_count").then(r => r.data);
export const adminCustomerThread = (id) => api.get(`/admin/customers/${id}/messages`).then(r => r.data);
export const adminSendSiteMessage = (id, text) => api.post(`/admin/customers/${id}/messages`, { text }).then(r => r.data);

// Products
export const getProducts = (params) => api.get("/products", { params }).then(r => r.data);
export const getProduct = (id) => api.get(`/products/${id}`).then(r => r.data);
export const createProduct = (data) => api.post("/products", data).then(r => r.data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data).then(r => r.data);
export const deleteProduct = (id) => api.delete(`/products/${id}`).then(r => r.data);
export const approveProduct = (id) => api.post(`/products/${id}/approve`).then(r => r.data);
export const importProductsBackup = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 }).then(r => r.data);
};
export const importCustomersBackup = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 }).then(r => r.data);
};

// Upload
export const uploadFile = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};

// Orders
export const createOrder = (data) => api.post("/orders", data).then(r => r.data);
export const getOrders = (params) => api.get("/orders", { params }).then(r => r.data);
export const updateOrder = (id, status) => api.put(`/orders/${id}?status=${status}`).then(r => r.data);

// Events
export const getEvents = (params) => api.get("/events", { params }).then(r => r.data);
export const createEvent = (data) => api.post("/events", data).then(r => r.data);
export const updateEvent = (id, data) => api.put(`/events/${id}`, data).then(r => r.data);
export const deleteEvent = (id) => api.delete(`/events/${id}`).then(r => r.data);
export const approveEvent = (id) => api.post(`/events/${id}/approve`).then(r => r.data);
export const rejectEvent = (id) => api.post(`/events/${id}/reject`).then(r => r.data);

// Categories
export const getCategories = () => api.get("/categories").then(r => r.data);
export const createCategory = (data) => api.post("/categories", data).then(r => r.data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data).then(r => r.data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`).then(r => r.data);
export const reorderCategories = (order) => api.post("/categories/reorder", { order }).then(r => r.data);

// Promo
export const checkPromo = (code) => api.get(`/promo/check/${code}`).then(r => r.data);
export const getPromos = () => api.get("/promo").then(r => r.data);
export const createPromo = (data) => api.post("/promo", data).then(r => r.data);
export const deletePromo = (id) => api.delete(`/promo/${id}`).then(r => r.data);
export const getPromoReferrals = (id) => api.get(`/promo/${id}/referrals`).then(r => r.data);

// Admin
export const adminLogin = (data) => api.post("/admin/login", data).then(r => r.data);
export const adminVerify = () => api.get("/admin/verify").then(r => r.data);
export const admin2faAuth = (data) => api.post("/admin/2fa/authenticate", data).then(r => r.data);
export const admin2faSetup = () => api.post("/admin/2fa/setup").then(r => r.data);
export const admin2faVerify = (code) => api.post("/admin/2fa/verify", { code }).then(r => r.data);
export const admin2faDisable = (code) => api.post("/admin/2fa/disable", { code }).then(r => r.data);
export const adminChangePassword = (data) => api.post("/admin/change-password", data).then(r => r.data);

// Orders export
export const exportOrders = (params) => api.get("/orders/export", { params, responseType: "blob" }).then(r => r.data);

// Bot
export const getBotSettings = () => api.get("/bot/settings").then(r => r.data);
export const updateBotSettings = (data) => api.put("/bot/settings", data).then(r => r.data);
export const setupBot = () => api.post("/bot/setup").then(r => r.data);

// Settings
export const getSettings = () => api.get("/settings").then(r => r.data);export const updateSettings = (data) => api.put("/settings", data).then(r => r.data);
export const getConfig = () => api.get("/config").then(r => r.data);
export const refreshExchangeRates = () => api.post("/settings/refresh-rates").then(r => r.data);

// Payments (bePaid stub)
export const getPaymentConfig = () => axios.get(`${API_BASE}/payment/config`).then(r => r.data);
export const getPaymentSettings = () => api.get("/admin/payment/settings").then(r => r.data);
export const updatePaymentSettings = (data) => api.put("/admin/payment/settings", data).then(r => r.data);

// Cloudinary
export const getCloudinarySettings = () => api.get("/cloudinary/settings").then(r => r.data);
export const updateCloudinarySettings = (data) => api.put("/cloudinary/settings", data).then(r => r.data);
export const getCloudinaryStats = () => api.get("/cloudinary/stats").then(r => r.data);
export const cloudinaryCleanup = () => api.post("/cloudinary/cleanup").then(r => r.data);
export const cloudinarySync = () => api.post("/cloudinary/sync").then(r => r.data);

// Database backup (full JSON export)
export const downloadBackup = () => api.get("/admin/backup", { responseType: "blob" }).then(r => r);
export const sendBackupToTelegram = () => api.post("/admin/backup/telegram").then(r => r.data);
export const restoreBackup = (file, code) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("code", code);
  fd.append("confirm", "true");
  return api.post("/admin/backup/restore", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  }).then(r => r.data);
};
export const downloadPhotoArchive = () => api.get("/admin/photos/archive", { responseType: "blob", timeout: 600000 }).then(r => r);
export const startPhotoArchive = () => api.post("/admin/photos/archive/start").then(r => r.data);
export const getPhotoArchiveStatus = (jobId) => api.get(`/admin/photos/archive/status/${jobId}`).then(r => r.data);
export const getPhotoArchiveDownloadToken = (jobId) => api.post(`/admin/photos/archive/token/${jobId}`).then(r => r.data);
export const downloadPhotoArchiveJob = (jobId) => api.get(`/admin/photos/archive/download/${jobId}`, { responseType: "blob", timeout: 600000 }).then(r => r);

export const getImageUrl = (path, width) => {
  if (!path) return "/placeholder.svg";
  // Cloudinary URLs: inject f_auto,q_auto (auto format + quality) and optional width
  // This dramatically reduces bandwidth: original JPG 142KB → WebP w_400 ~19KB (-87%)
  if (path.includes("res.cloudinary.com") && path.includes("/upload/")) {
    const transforms = ["f_auto", "q_auto"];
    if (width) transforms.push(`w_${width}`, "c_limit");
    return path.replace("/upload/", `/upload/${transforms.join(",")}/`);
  }
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}${path}`;
};

// Image with fallback
export const ImageWithFallback = ({ src, alt, className, width, ...props }) => {
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  const imgSrc = error ? "/placeholder.svg" : getImageUrl(src, width);

  return (
    <img
      src={imgSrc}
      alt={alt || "Image"}
      className={className}
      onError={() => setError(true)}
      onLoad={() => setLoading(false)}
      style={{ opacity: loading && !error ? 0.5 : 1 }}
      {...props}
    />
  );
};

export default api;